import { useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { SectionHeading } from "./SectionHeading";
import { Reveal, EASE } from "./motion-primitives";
import { StyleChip } from "./discoverography-cards";
import { DestinationTile, RegionCard } from "./destinations-components";
import { REAL_DESTINATIONS, INTENTIONS } from "./destinations-catalog";
import { useSeo } from "./useSeo";

export const DestinationsPage = () => {
  useSeo({
    title: "Destinations — Somewhere Worth Going | Holideviser",
    description:
      "Curated destinations with a point of view. Discover where to go, why, who it's for and how Holideviser would experience it.",
  });

  const [region, setRegion] = useState(null);
  const [intention, setIntention] = useState(null);
  const galleryRef = useRef(null);

  // Directory reflects only the destinations actually displayed (the real, founder-aligned set).
  const DESTINATIONS = REAL_DESTINATIONS;
  const DEST_REGIONS = useMemo(
    () => Array.from(new Set(DESTINATIONS.map((d) => d.region))),
    [DESTINATIONS],
  );
  const DISPLAY_INTENTIONS = useMemo(() => {
    const present = new Set(DESTINATIONS.flatMap((d) => d.intentions));
    return INTENTIONS.filter((i) => present.has(i.id));
  }, [DESTINATIONS]);

  const regionImage = useMemo(() => {
    const map = {};
    DEST_REGIONS.forEach((r) => {
      map[r] = DESTINATIONS.find((d) => d.region === r)?.image;
    });
    return map;
  }, [DEST_REGIONS, DESTINATIONS]);
  const regionCount = useMemo(() => {
    const map = {};
    DEST_REGIONS.forEach((r) => {
      map[r] = DESTINATIONS.filter((d) => d.region === r).length;
    });
    return map;
  }, [DEST_REGIONS, DESTINATIONS]);

  const filtered = useMemo(
    () =>
      DESTINATIONS.filter(
        (d) =>
          (!region || d.region === region) &&
          (!intention || d.intentions.includes(intention)),
      ),
    [region, intention, DESTINATIONS],
  );

  const scrollToGallery = () => {
    if (window.__lenis && galleryRef.current)
      window.__lenis.scrollTo(galleryRef.current, { offset: -80, duration: 1.1 });
    else galleryRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const pickRegion = (r) => {
    setRegion((x) => (x === r ? null : r));
    setTimeout(scrollToGallery, 60);
  };
  const pickIntention = (id) => {
    setIntention((x) => (x === id ? null : id));
    setTimeout(scrollToGallery, 60);
  };
  const anyFilter = region || intention;
  const clearAll = () => {
    setRegion(null);
    setIntention(null);
  };
  const intentionLabel = INTENTIONS.find((i) => i.id === intention)?.label;

  return (
    <div className="min-h-screen bg-ink">
      <Navigation />

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 pt-40 sm:px-10 lg:px-16 lg:pt-52">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-teal" />
            <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal">
              Destinations
            </span>
          </div>
          <h1
            data-testid="destinations-hero-headline"
            className="max-w-4xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl"
          >
            Somewhere <span className="italic text-teal">worth going.</span>
          </h1>
          <p className="mt-8 max-w-xl font-sans text-base leading-relaxed text-sand sm:text-lg">
            Explore our growing collection of personally experienced places. These edits reflect where we
            have firsthand recommendations — not every destination we can help you plan.
          </p>
        </motion.div>
      </section>

      {/* By region */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 lg:px-16 lg:py-32">
        <SectionHeading overline="Browse by region" title="Start with the map." testId="region-heading" />
        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:mt-20 lg:grid-cols-3">
          {DEST_REGIONS.map((r) => (
            <RegionCard
              key={r}
              region={r}
              image={regionImage[r]}
              count={regionCount[r]}
              active={region === r}
              onClick={pickRegion}
            />
          ))}
        </div>
      </section>

      {/* By intention */}
      <section className="border-y border-charcoal bg-charcoal/20">
        <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 lg:px-16 lg:py-24">
          <SectionHeading
            overline="Browse by intention"
            title="Or start with what you want."
            intro="Browse these destination edits by the kind of trip you have in mind."
            testId="intention-heading"
          />
          <Reveal className="mt-10 flex flex-wrap gap-3 lg:mt-12">
            {DISPLAY_INTENTIONS.map((i) => (
              <StyleChip
                key={i.id}
                style={i}
                active={intention === i.id}
                onClick={pickIntention}
              />
            ))}
          </Reveal>
        </div>
      </section>

      {/* Featured / filtered grid */}
      <section ref={galleryRef} className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 lg:px-16">
          <SectionHeading
            overline="Featured destinations"
            title={anyFilter ? "Places that fit." : "Where we'd send you."}
            testId="featured-heading"
          />

          {(anyFilter) && (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {region && <FilterTag label={`Region: ${region}`} onClear={() => setRegion(null)} />}
              {intentionLabel && (
                <FilterTag label={intentionLabel} onClear={() => setIntention(null)} />
              )}
              <button
                onClick={clearAll}
                data-testid="dest-clear-filters"
                className="font-sans text-xs uppercase tracking-[0.18em] text-sand underline-offset-4 hover:text-teal hover:underline"
              >
                Clear all
              </button>
            </div>
          )}

          {filtered.length > 0 ? (
            <div
              className="mt-12 grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3"
              data-testid="featured-grid"
            >
              {filtered.map((d) => (
                <DestinationTile key={d.slug} d={d} />
              ))}
            </div>
          ) : (
            <div className="mt-16 border border-charcoal py-20 text-center" data-testid="dest-no-results">
              <p className="font-serif text-2xl font-light italic text-cream/80">
                Nothing matches that exact combination — yet.
              </p>
              <button
                onClick={clearAll}
                className="mt-6 font-sans text-xs uppercase tracking-[0.18em] text-teal hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Somewhere else? enquiry block */}
      <section className="border-t border-charcoal bg-charcoal/20" data-testid="dest-enquiry-block">
        <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 lg:px-16 lg:py-24">
          <Reveal>
            <h2 className="max-w-2xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl">
              Somewhere else on your mind?
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-6 max-w-xl font-sans text-base leading-relaxed text-sand sm:text-lg">
              Tell us where you'd like to go. We'll confirm whether we can help shape the trip with our
              destination partners.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <Link
              to="/plan-my-trip"
              data-testid="dest-enquiry-cta"
              className="mt-8 inline-block rounded-none bg-teal px-8 py-4 font-sans text-sm uppercase tracking-[0.18em] text-ink transition-colors hover:bg-cream"
            >
              Plan My Trip
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FilterTag = ({ label, onClear }) => (
  <span className="inline-flex items-center gap-2 border border-charcoal px-4 py-2 font-sans text-xs text-cream">
    {label}
    <button onClick={onClear} aria-label="Remove filter" className="text-sand hover:text-teal">
      <X size={13} />
    </button>
  </span>
);
